import React, { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Calendar, DollarSign, TrendingUp, Eye, Download, Clock, Ban, Phone, ClipboardList, Link2 } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Section, Button, Badge, LoadingSpinner, Report1099Modal, CallModal } from './components';

// Date utility functions
const formatDateLocal = (date) => {
  // Format date as YYYY-MM-DD in local timezone (not UTC)
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getCurrentDate = () => {
  return formatDateLocal(new Date());
};

// ISO week number calculation helper
const getISOWeekNumber = (date) => {
  const target = new Date(date.valueOf());
  const dayNum = (date.getDay() + 6) % 7; // Monday=0, Sunday=6
  target.setDate(target.getDate() - dayNum + 3); // Set to Thursday of current week
  const firstThursday = new Date(target.getFullYear(), 0, 4); // Jan 4 is always in week 1
  return 1 + Math.round(((target - firstThursday) / 86400000) / 7);
};

const getCurrentWeek = () => {
  return getISOWeekNumber(new Date());
};

const getCurrentMonth = () => {
  return new Date().getMonth() + 1;
};

const getCurrentYear = () => {
  return new Date().getFullYear();
};

const getWeekNumber = (date) => {
  return getISOWeekNumber(new Date(date));
};

function TechnicianDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [technician, setTechnician] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    zip_code: '',
    location_id: '',
    internal_status: 'active',
    max_travel_distance: ''
  });

  // Availability states
  const [daysOff, setDaysOff] = useState([]);
  const [loadingDaysOff, setLoadingDaysOff] = useState(false);

  // Appointment filter states
  const [dateFilterType, setDateFilterType] = useState('week'); // 'date', 'week', 'month'
  const [selectedDate, setSelectedDate] = useState(getCurrentDate());
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [selectedType, setSelectedType] = useState('all'); // 'all', 'standard', 'alf', 'event'
  const [show1099Modal, setShow1099Modal] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (id) {
      fetchTechnicianDetails();
      fetchLocations();
      fetchDaysOff();
    }
  }, [id]);

  const fetchTechnicianDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getStaffMember(id);
      setTechnician(data);

      // Populate form with technician data
      setFormData({
        first_name: data.person?.first_name || '',
        last_name: data.person?.last_name || '',
        phone_number: data.person?.phone_number || '',
        zip_code: data.person?.zip_code || '',
        email: data.person?.email || '',
        location_id: data.location?.id || '',
        internal_status: data.internal_status || 'active',
        max_travel_distance: data.max_travel_distance ?? ''
      });
    } catch (err) {
      setError(err.message || 'Failed to fetch team member details');
      console.error('Error fetching technician:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await apiService.getLocations();
      setLocations(data.results || data || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchDaysOff = async () => {
    try {
      setLoadingDaysOff(true);
      const today = new Date();
      const dateFrom = formatDateLocal(today);
      // Fetch next 90 days
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() + 90);
      const dateTo = formatDateLocal(endDate);
      const data = await apiService.getTechnicianDaysOff({
        technician: id,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setDaysOff(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('Error fetching days off:', err);
    } finally {
      setLoadingDaysOff(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'location_id' ? parseInt(value, 10) : name === 'max_travel_distance' ? value.replace(/\D/g, '') : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);

      // Update person data
      if (technician.person?.id) {
        await apiService.patch(`/amn/persons/${technician.person.id}/`, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone_number: formData.phone_number,
          email: formData.email,
          zip_code: formData.zip_code
        });
      }

      // Update technician data
      await apiService.updateStaff(id, {
        location_id: formData.location_id,
        internal_status: formData.internal_status,
        max_travel_distance: formData.max_travel_distance === '' ? null : parseInt(formData.max_travel_distance, 10)
      });

      setIsEditing(false);
      fetchTechnicianDetails();
    } catch (err) {
      alert(err.message || 'Could not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const createMobileCredentials = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/amn/create_technician_user/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ technician_id: id })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Login created successfully! Username: ${data.username || 'Check system'}`);
      } else {
        const errorData = await response.json();
        alert('Failed to create login: ' + (errorData.error || 'Unknown error'));
      }
    } catch (err) {
      alert('Error creating mobile credentials: ' + err.message);
    }
  };

  // Filter appointments based on selected filters
  const filteredAppointments = useMemo(() => {
    if (!technician?.appointments) return [];

    return technician.appointments.filter(appt => {
      const apptDate = new Date(appt.date_time);

      // Date filter
      if (dateFilterType === 'date') {
        const apptDateStr = formatDateLocal(apptDate);
        if (apptDateStr !== selectedDate) return false;
      } else if (dateFilterType === 'week') {
        const apptWeek = getWeekNumber(apptDate);
        const apptYear = apptDate.getFullYear();
        if (apptWeek !== selectedWeek || apptYear !== selectedYear) return false;
      } else if (dateFilterType === 'month') {
        const apptMonth = apptDate.getMonth() + 1;
        const apptYear = apptDate.getFullYear();
        if (apptMonth !== selectedMonth || apptYear !== selectedYear) return false;
      }

      // Type filter
      if (selectedType !== 'all') {
        if (selectedType === 'alf' && !appt.is_alf) return false;
        if (selectedType === 'event' && !appt.is_event) return false;
        if (selectedType === 'standard' && (appt.is_alf || appt.is_event)) return false;
      }

      return true;
    });
  }, [technician?.appointments, dateFilterType, selectedDate, selectedWeek, selectedMonth, selectedYear, selectedType]);

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Header with Back Button */}
        <div className="mb-3 flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={() => navigate('/technicians')}>
            <ArrowLeft className="w-3.5 h-3.5 mr-1" />
            Back
          </Button>
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Team Member Details</h1>
        </div>

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Error State */}
        {error && !loading && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-4 w-4 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-2">
                <h3 className="text-xs font-medium text-red-800">Error loading team member</h3>
                <p className="mt-0.5 text-xs text-red-700">{error}</p>
                <button
                  onClick={fetchTechnicianDetails}
                  className="mt-1 text-xs font-medium text-red-800 hover:text-red-900"
                >
                  Try again
                </button>
              </div>
            </div>
          </Panel>
        )}

        {/* Content */}
        {!loading && !error && technician && (
          <div className="space-y-3">
            {/* Technician Information */}
            <Panel>
              <Section title="Team Member Information">
                {!isEditing ? (
                  /* Read-only view */
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="text-sm font-medium text-gray-900">
                          {technician.person?.first_name || ''} {technician.person?.last_name || ''}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Phone</p>
                        <div className="flex items-center gap-1">
                          <p className="text-sm text-gray-900">{technician.person?.phone_number || 'N/A'}</p>
                          {technician.person?.phone_number && (
                            <button
                              onClick={() => setShowCallModal(true)}
                              className="text-green-600 hover:text-green-800 transition-colors"
                              title="Call team member"
                            >
                              <Phone className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Email</p>
                        <p className="text-sm text-gray-900">{technician.person?.email || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Zip Code</p>
                        <p className="text-sm text-gray-900">{technician.person?.zip_code || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Max Travel Distance</p>
                        {technician.max_travel_distance != null ? (
                          <p className="text-sm text-gray-900">{technician.max_travel_distance} miles</p>
                        ) : (
                          <Badge variant="warning" small>Not set</Badge>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <Badge variant={technician.internal_status === 'active' ? 'success' : 'neutral'} small>
                          {technician.internal_status || 'N/A'}
                        </Badge>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Location</p>
                        <p className="text-sm text-gray-900">{technician.location?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Team Member ID</p>
                        <p className="text-sm text-gray-900">{technician.id}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <Button onClick={() => setIsEditing(true)} size="sm" variant="secondary">
                        Edit
                      </Button>
                      <Button onClick={() => setShow1099Modal(true)} size="sm" variant="secondary">
                        <Download className="w-3.5 h-3.5 mr-1" />
                        Download 1099
                      </Button>
                      <Button onClick={createMobileCredentials} size="sm" className="bg-yellow-500 hover:bg-yellow-600 text-red-900">
                        Create Mobile Credentials
                      </Button>
                      <Button size="sm" variant="secondary" onClick={async () => {
                        try {
                          const data = await apiService.getIntakeLink(technician.id);
                          await navigator.clipboard.writeText(data.link);
                          alert('Intake form link copied!\n\n' + data.link);
                        } catch (err) { alert('Failed to generate link'); }
                      }}>
                        <ClipboardList className="w-3.5 h-3.5 mr-1" />
                        Intake Form Link
                      </Button>
                      {technician.qualifications && (
                        <span className="flex items-center text-xs text-green-600 gap-1">
                          <ClipboardList className="w-3 h-3" /> Intake completed
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  /* Edit form */
                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      <div>
                        <label htmlFor="first_name" className="block text-xs font-medium text-gray-700 mb-1">
                          First Name
                        </label>
                        <input
                          type="text"
                          id="first_name"
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="last_name" className="block text-xs font-medium text-gray-700 mb-1">
                          Last Name
                        </label>
                        <input
                          type="text"
                          id="last_name"
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="phone_number" className="block text-xs font-medium text-gray-700 mb-1">
                          Phone
                        </label>
                        <input
                          type="tel"
                          id="phone_number"
                          name="phone_number"
                          value={formData.phone_number}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                        />
                      </div>
                      <div>
                        <label htmlFor="zip_code" className="block text-xs font-medium text-gray-700 mb-1">
                          Zip Code
                        </label>
                        <input
                          type="text"
                          id="zip_code"
                          name="zip_code"
                          value={formData.zip_code}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                        />
                      </div>
                      <div>
                        <label htmlFor="max_travel_distance" className="block text-xs font-medium text-gray-700 mb-1">
                          Max Travel Distance (miles)
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          id="max_travel_distance"
                          name="max_travel_distance"
                          value={formData.max_travel_distance}
                          onChange={handleChange}
                          placeholder="e.g. 30"
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                        />
                      </div>
                      <div>
                        <label htmlFor="internal_status" className="block text-xs font-medium text-gray-700 mb-1">
                          Status
                        </label>
                        <select
                          id="internal_status"
                          name="internal_status"
                          value={formData.internal_status}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                      <div>
                        <label htmlFor="location_id" className="block text-xs font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <select
                          id="location_id"
                          name="location_id"
                          value={formData.location_id}
                          onChange={handleChange}
                          className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                          required
                        >
                          <option value="">Select a location</option>
                          {locations.map((location) => (
                            <option key={location.id} value={location.id}>
                              {location.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="flex gap-2 pt-2 border-t border-gray-100">
                      <Button type="submit" disabled={saving} size="sm">
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                      <Button type="button" onClick={() => setIsEditing(false)} size="sm" variant="secondary">
                        Cancel
                      </Button>
                    </div>
                  </form>
                )}
              </Section>
            </Panel>

            {/* Intake / Qualifications */}
            {technician.qualifications && (
              <Panel>
                <Section title="Intake Form Results">
                  {(() => {
                    const q = technician.qualifications;
                    const renderChecks = (items, data) => (
                      <div className="flex flex-wrap gap-1.5">
                        {items.filter(i => data?.[i.key]).map(i => (
                          <span key={i.key} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/></svg>
                            {i.label}
                          </span>
                        ))}
                        {items.filter(i => !data?.[i.key]).map(i => (
                          <span key={i.key} className="text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-400 border border-gray-200">{i.label}</span>
                        ))}
                      </div>
                    );
                    const comfortItems = [
                      { key: 'seniors', label: 'Seniors' }, { key: 'challenging_feet', label: 'Challenging feet' },
                      { key: 'hospital_visits', label: 'Hospital' }, { key: 'alf_facilities', label: 'ALF' },
                      { key: 'bedside_service', label: 'Bedside' }, { key: 'events', label: 'Events' },
                      { key: 'male_clients', label: 'Male clients' }, { key: 'children', label: 'Children' },
                    ];
                    const skillItems = [
                      { key: 'basic_manicure', label: 'Basic Mani' }, { key: 'basic_pedicure', label: 'Basic Pedi' },
                      { key: 'gel_polish', label: 'Gel Polish' }, { key: 'gel_x', label: 'Gel-X' },
                      { key: 'builder_gel', label: 'Builder Gel' }, { key: 'dip_powder', label: 'Dip Powder' },
                      { key: 'nail_art', label: 'Nail Art' }, { key: 'acrylic', label: 'Acrylic' },
                      { key: 'acrylic_removal', label: 'Removal' }, { key: 'therapeutic_foot_care', label: 'Foot Care' },
                      { key: 'massage', label: 'Massage' }, { key: 'spa_pedicure', label: 'Spa Pedi' },
                    ];
                    return (
                      <div className="space-y-3">
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1.5">Comfort & Willingness</p>
                          {renderChecks(comfortItems, q.comfort)}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 mb-1.5">Skills & Techniques</p>
                          {renderChecks(skillItems, q.skills)}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div>
                            <p className="text-xs text-gray-500">Experience</p>
                            <p className="text-sm font-medium text-gray-900">{q.experience_years ? `${q.experience_years} years` : '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Max Travel</p>
                            <p className="text-sm font-medium text-gray-900">{q.travel?.max_distance_miles ? `${q.travel.max_distance_miles} mi` : '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Home Zip</p>
                            <p className="text-sm font-medium text-gray-900">{q.travel?.home_zip_code || '—'}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Transportation</p>
                            <p className="text-sm font-medium text-gray-900">{q.travel?.has_transportation ? 'Yes' : 'No'}</p>
                          </div>
                        </div>
                        {q.availability && Object.values(q.availability).some(Boolean) && (
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Availability</p>
                            <div className="flex flex-wrap gap-1">
                              {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                                <span key={d} className={`text-xs px-2 py-0.5 rounded ${q.availability[d] ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
                                  {d.charAt(0).toUpperCase() + d.slice(1, 3)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {q.notes && (
                          <div>
                            <p className="text-xs text-gray-500">Notes</p>
                            <p className="text-sm text-gray-700">{q.notes}</p>
                          </div>
                        )}
                        {q.completed_at && (
                          <p className="text-[10px] text-gray-400">Submitted {new Date(q.completed_at).toLocaleDateString()}</p>
                        )}
                      </div>
                    );
                  })()}
                </Section>
              </Panel>
            )}

            {/* Square Integration Details */}
            <Panel>
              <Section title="Square Integration">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Square ID</p>
                    <p className="text-xs font-medium text-gray-900">{technician.square_id || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Square Status</p>
                    <p className="text-xs font-medium text-gray-900">{technician.square_status || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Is Owner</p>
                    {technician.is_owner ? (
                      <Badge variant="success" small>Yes</Badge>
                    ) : (
                      <Badge variant="neutral" small>No</Badge>
                    )}
                  </div>
                </div>
              </Section>
            </Panel>

            {/* Availability / Days Off */}
            <Panel>
              <Section title="Availability (Next 90 Days)">
                {loadingDaysOff ? (
                  <LoadingSpinner />
                ) : daysOff.length === 0 ? (
                  <p className="text-xs text-gray-500">No blocked dates in the next 90 days.</p>
                ) : (
                  <div className="space-y-1.5">
                    {daysOff.map((block) => {
                      const d = new Date(block.date + 'T12:00:00');
                      const dateLabel = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                      const isAllDay = block.block_type === 'all_day';
                      return (
                        <div key={block.id} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${isAllDay ? 'bg-red-50 border border-red-200' : 'bg-orange-50 border border-orange-200'}`}>
                          {isAllDay ? (
                            <Ban className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                          ) : (
                            <Clock className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />
                          )}
                          <span className="font-medium text-gray-900">{dateLabel}</span>
                          {isAllDay ? (
                            <Badge variant="error" small>All Day Off</Badge>
                          ) : (
                            <span className="text-gray-600">
                              {block.start_time?.slice(0, 5)} - {block.end_time?.slice(0, 5)}
                            </span>
                          )}
                          {block.note && (
                            <span className="text-gray-400 truncate">— {block.note}</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </Section>
            </Panel>

            {/* Statistics Overview */}
            {technician.appointments && (
              <>
                <Panel>
                  <Section title="Performance Metrics">
                    {/* Weekly Averages - Important metrics on top */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                      <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                        <div className="flex items-center justify-between mb-2">
                          <Calendar className="w-4 h-4 text-red-600" />
                          <span className="text-xs text-red-600 font-medium">Last 4 weeks</span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Avg Weekly Appointments</p>
                        <p className="text-xl font-bold text-gray-900">{technician.avg_weekly_appointments_4w || 0}</p>
                      </div>

                      <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                        <div className="flex items-center justify-between mb-2">
                          <DollarSign className="w-4 h-4 text-red-600" />
                          <span className="text-xs text-red-600 font-medium">Last 10 weeks</span>
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Avg Weekly Payout</p>
                        <p className="text-xl font-bold text-gray-900">${(technician.avg_weekly_payroll_10w || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>

                    {/* All-time totals */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {/* Total Appointments */}
                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Calendar className="w-4 h-4 text-blue-600" />
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Total Appointments</p>
                        <p className="text-lg font-bold text-gray-900">{(technician.appointments?.length || 0).toLocaleString()}</p>
                      </div>

                      {/* Total Revenue */}
                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <DollarSign className="w-4 h-4 text-green-600" />
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Total Revenue</p>
                        <p className="text-lg font-bold text-gray-900">
                          ${technician.appointments?.reduce((sum, appt) => sum + (parseFloat(appt.total_payment || appt.expected_revenue) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>

                      {/* Total Payout */}
                      <div className="bg-purple-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <TrendingUp className="w-4 h-4 text-purple-600" />
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Total Payout</p>
                        <p className="text-lg font-bold text-gray-900">
                          ${technician.appointments?.reduce((sum, appt) => sum + (parseFloat(appt.technician_payout) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>

                      {/* Total Profit */}
                      <div className="bg-yellow-50 p-3 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <TrendingUp className="w-4 h-4 text-yellow-600" />
                        </div>
                        <p className="text-xs text-gray-600 mb-1">Total Profit</p>
                        <p className="text-lg font-bold text-gray-900">
                          ${(technician.appointments?.reduce((sum, appt) => sum + (parseFloat(appt.expected_profit) || 0), 0) || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                        </p>
                      </div>
                    </div>
                  </Section>
                </Panel>

                {/* Appointments Table */}
                <Panel>
                  <Section title={`Appointments (${filteredAppointments.length} of ${technician.appointments?.length || 0})`}>
                    {/* Filter Controls */}
                    <div className="mb-3 space-y-2">
                      {/* Date Type Selector */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex gap-1 border border-gray-200 rounded">
                          <button
                            onClick={() => setDateFilterType('date')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              dateFilterType === 'date'
                                ? 'bg-red-800 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Date
                          </button>
                          <button
                            onClick={() => setDateFilterType('week')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              dateFilterType === 'week'
                                ? 'bg-red-800 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Week
                          </button>
                          <button
                            onClick={() => setDateFilterType('month')}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              dateFilterType === 'month'
                                ? 'bg-red-800 text-white'
                                : 'text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            Month
                          </button>
                        </div>

                        {/* Date Input */}
                        {dateFilterType === 'date' && (
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                          />
                        )}

                        {/* Week Input */}
                        {dateFilterType === 'week' && (
                          <>
                            <input
                              type="number"
                              min="1"
                              max="53"
                              value={selectedWeek}
                              onChange={(e) => setSelectedWeek(parseInt(e.target.value) || 1)}
                              placeholder="Week"
                              className="w-16 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                            />
                            <input
                              type="number"
                              min="2000"
                              max="2100"
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(parseInt(e.target.value) || getCurrentYear())}
                              placeholder="Year"
                              className="w-20 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                            />
                          </>
                        )}

                        {/* Month Input */}
                        {dateFilterType === 'month' && (
                          <>
                            <select
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                              className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                            >
                              <option value="1">January</option>
                              <option value="2">February</option>
                              <option value="3">March</option>
                              <option value="4">April</option>
                              <option value="5">May</option>
                              <option value="6">June</option>
                              <option value="7">July</option>
                              <option value="8">August</option>
                              <option value="9">September</option>
                              <option value="10">October</option>
                              <option value="11">November</option>
                              <option value="12">December</option>
                            </select>
                            <input
                              type="number"
                              min="2000"
                              max="2100"
                              value={selectedYear}
                              onChange={(e) => setSelectedYear(parseInt(e.target.value) || getCurrentYear())}
                              placeholder="Year"
                              className="w-20 px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                            />
                          </>
                        )}

                        {/* Type Filter */}
                        <select
                          value={selectedType}
                          onChange={(e) => setSelectedType(e.target.value)}
                          className="px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-red-800"
                        >
                          <option value="all">All Types</option>
                          <option value="standard">Standard</option>
                          <option value="alf">ALF</option>
                          <option value="event">Event</option>
                        </select>
                      </div>
                    </div>

                    <div className="overflow-x-auto -mx-4">
                      <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Client</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Date</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Type</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Discount</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Exp Rev</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Exp Profit</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Paid</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Amt Paid</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Match</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Tip</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Team Payout</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Review</th>
                            <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Details</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {filteredAppointments.map((appointment) => (
                            <tr key={appointment.id} className="hover:bg-gray-50">
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.client_name || 'Unknown'}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {new Date(appointment.date_time).toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' })}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.is_alf ? 'ALF' : appointment.is_event ? 'Event' : 'Standard'}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.discount ? `$${parseFloat(appointment.discount).toFixed(2)}` : '$0.00'}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.expected_revenue ? `$${parseFloat(appointment.expected_revenue).toFixed(2)}` : '$0.00'}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.expected_profit ? `$${parseFloat(appointment.expected_profit).toFixed(2)}` : '$0.00'}
                              </td>
                              <td className="px-2 py-1.5 text-xs">
                                {appointment.is_paid ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-red-600">✗</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.total_payment ? `$${parseFloat(appointment.total_payment).toFixed(2)}` : '$0.00'}
                              </td>
                              <td className="px-2 py-1.5 text-xs">
                                {appointment.total_payment && appointment.expected_revenue ? (
                                  parseFloat(appointment.total_payment) === parseFloat(appointment.expected_revenue) ? (
                                    <span className="text-green-600">✓</span>
                                  ) : (
                                    <span className="text-red-600">✗</span>
                                  )
                                ) : (
                                  <span className="text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.technician_tip ? `$${parseFloat(appointment.technician_tip).toFixed(2)}` : '$0.00'}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-gray-900">
                                {appointment.technician_payout ? `$${parseFloat(appointment.technician_payout).toFixed(2)}` : '$0.00'}
                              </td>
                              <td className="px-2 py-1.5 text-xs">
                                {appointment.has_review ? (
                                  <span className="text-green-600">✓</span>
                                ) : (
                                  <span className="text-red-600">✗</span>
                                )}
                              </td>
                              <td className="px-2 py-1.5 text-xs">
                                <button
                                  onClick={() => navigate(`/appointments/${appointment.id}`)}
                                  className="text-gray-400 hover:text-red-800 transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {filteredAppointments.length === 0 && (
                            <tr>
                              <td colSpan="13" className="px-2 py-4 text-center text-xs text-gray-500">
                                No appointments found for selected filters
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </Section>
                </Panel>

              </>
            )}
          </div>
        )}
      </div>

      {/* 1099 Report Modal */}
      {show1099Modal && technician && (
        <Report1099Modal
          technician={technician}
          onClose={() => setShow1099Modal(false)}
        />
      )}

      <CallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        phoneNumber={technician?.person?.phone_number}
        personName={technician?.person ? `${technician.person.first_name || ''} ${technician.person.last_name || ''}`.trim() : null}
        personId={technician?.person?.id}
        apiService={apiService}
      />
    </div>
  );
}

export default TechnicianDetailPage;
